/**
 * Local preview of the onboarding stepper (internal route /onboarding-preview).
 *
 * Renders OnboardingFlow with mock questions and a stubbed submit so the UI can
 * be reviewed with `npm start` — no backend, no Shopify session, and no
 * pre-computed shelves required. Not exposed on CDN domains.
 */

import { CheckCircle2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import type { OnboardingQuestion, OnboardRequestBody, RecommendationResult } from '../services/api';
import OnboardingFlow from './OnboardingFlow';

const MOCK_GENRES: Array<{ value: string; audiences: string[] }> = [
  { value: 'FICTION / Fantasy', audiences: ['ADULT'] },
  { value: 'FICTION / Mystery & Detective', audiences: ['ADULT'] },
  { value: 'FICTION / Romance', audiences: ['ADULT'] },
  { value: 'FICTION / Science Fiction', audiences: ['ADULT'] },
  { value: 'FICTION / Literary', audiences: ['ADULT'] },
  { value: 'FICTION / Thrillers', audiences: ['ADULT'] },
  { value: 'BIOGRAPHY & AUTOBIOGRAPHY', audiences: ['ADULT'] },
  { value: 'HISTORY', audiences: ['ADULT'] },
  { value: 'SELF-HELP', audiences: ['ADULT'] },
  { value: 'COOKING', audiences: ['ADULT'] },
  { value: 'YOUNG ADULT FICTION / Fantasy', audiences: ['TEEN'] },
  { value: 'YOUNG ADULT FICTION / Romance', audiences: ['TEEN'] },
  { value: 'JUVENILE FICTION / Animals', audiences: ['KIDS'] },
  { value: 'JUVENILE FICTION / Fantasy & Magic', audiences: ['KIDS'] },
];

const MOCK_THEMES = [
  'Adventure',
  'Found family',
  'Slow burn',
  'Dark academia',
  'Cozy',
  'Enemies to lovers',
];

const MOCK_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'recipient_type',
    i18n_key: 'onboarding.q.recipient',
    type: 'multi',
    required: true,
    min: 1,
    options: [
      { value: 'self', i18n_key: 'onboarding.recipient.self' },
      { value: 'teen', i18n_key: 'onboarding.recipient.teen' },
      { value: 'kids', i18n_key: 'onboarding.recipient.kids' },
      { value: 'gift', i18n_key: 'onboarding.recipient.gift' },
    ],
  },
  {
    id: 'genres',
    i18n_key: 'onboarding.q.genres',
    type: 'multi',
    required: true,
    min: 3,
    max: 6,
    options: MOCK_GENRES.map((g) => ({
      value: g.value,
      label: g.value.split('/').pop()?.trim() ?? g.value,
      audiences: g.audiences,
    })),
  },
  {
    id: 'themes',
    i18n_key: 'onboarding.q.themes',
    type: 'multi',
    max: 5,
    options: MOCK_THEMES.map((tname) => ({ value: tname, label: tname })),
  },
  {
    id: 'languages',
    i18n_key: 'onboarding.q.languages',
    type: 'multi',
    required: true,
    min: 1,
    options: [
      { value: 'en', i18n_key: 'onboarding.language.en' },
      { value: 'fr', i18n_key: 'onboarding.language.fr' },
    ],
  },
];

const OnboardingPreview: React.FC = () => {
  const [submitted, setSubmitted] = useState<OnboardRequestBody | null>(null);

  // Stub the network call: capture the body and return a minimal non-empty
  // result so OnboardingFlow invokes onComplete.
  const mockSubmit = async (body: OnboardRequestBody): Promise<RecommendationResult> => {
    setSubmitted(body);
    return {
      shopify_customer_id: 'preview',
      customer_info: {},
      personalization_level: 'onboarding',
      status: 'ok',
      recommendations_by_profile: { Preview: [] as never[] },
      thinking_process: ['preview'],
    } as unknown as RecommendationResult;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <h1 className="text-2xl text-indigo-brand-text">Onboarding submitted</h1>
          <p className="text-indigo-brand-text/60 text-sm">
            This is a UI preview — the payload below is what the frontend sends to{' '}
            <code>POST /onboard</code>.
          </p>
          <pre className="text-left text-xs bg-indigo-brand-off-white border border-indigo-brand-border rounded-sm p-4 overflow-auto">
            {JSON.stringify(submitted, null, 2)}
          </pre>
          <button
            type="button"
            onClick={() => setSubmitted(null)}
            className="bg-indigo-brand-black text-white px-8 py-3 rounded-full font-medium hover:bg-black transition-colors"
          >
            Start over
          </button>
        </div>
      </div>
    );
  }

  return (
    <OnboardingFlow
      shopifyCustomerId="preview"
      questions={MOCK_QUESTIONS}
      customerFirstName="Alex"
      onComplete={() => undefined}
      onSubmit={mockSubmit}
    />
  );
};

export default OnboardingPreview;
